// Mapbox paint/style config for the realtime observation layers, plus the
// legend metadata derived from the same stops so the legend swatches can never
// drift out of sync with the map paint.

export const REALTIME_HOTSPOTS_LAYER_ID = "realtime-hotspots";
export const REALTIME_LIGHTNING_LAYER_ID = "realtime-lightning";
export const REALTIME_HAZARDS_LAYER_ID = "realtime-hazards";
export const REALTIME_IGNITION_POINTS_LAYER_ID = "realtime-ignition-points";
export const REALTIME_BURNT_AREAS_LAYER_ID = "realtime-burnt-areas";

export interface AgeColorStop {
  minutes: number;
  color: string;
  label: string;
}

export const HOTSPOT_AGE_COLOR_STOPS: AgeColorStop[] = [
  { minutes: 0, color: "#ff2d00", label: "Now" },
  { minutes: 60, color: "#ff7a00", label: "1 h" },
  { minutes: 360, color: "#ffc53d", label: "6 h" },
  { minutes: 1440, color: "#9aa0a6", label: "24 h+" },
];

// Lightning encodes two variables in the fill: hue = polarity, fade = age.
// (Polarity used to be the circle stroke, but the spots render without an
// outline now.) Both ramps converge on the same grey so age stays comparable
// across polarities.
export const LIGHTNING_POSITIVE_AGE_STOPS: AgeColorStop[] = [
  { minutes: 0, color: "#ffb300", label: "Now" },
  { minutes: 60, color: "#dd8f14", label: "1 h" },
  { minutes: 360, color: "#b08a52", label: "6 h" },
  { minutes: 1440, color: "#9aa0a6", label: "24 h+" },
];

export const LIGHTNING_NEGATIVE_AGE_STOPS: AgeColorStop[] = [
  { minutes: 0, color: "#4fc3f7", label: "Now" },
  { minutes: 60, color: "#2f9fd4", label: "1 h" },
  { minutes: 360, color: "#6690a5", label: "6 h" },
  { minutes: 1440, color: "#9aa0a6", label: "24 h+" },
];

// Customer ignition points: a magenta ramp so a reported fire start is never
// mistaken for a satellite pixel (orange) or a strike (blue/amber).
export const IGNITION_AGE_COLOR_STOPS: AgeColorStop[] = [
  { minutes: 0, color: "#ff2d95", label: "Now" },
  { minutes: 60, color: "#e0529b", label: "1 h" },
  { minutes: 360, color: "#b8749b", label: "6 h" },
  { minutes: 1440, color: "#9aa0a6", label: "24 h+" },
];

// Customer burnt areas: a dark red fading to grey with age, so a perimeter
// still being updated reads hotter than one nobody has touched all day.
export const BURNT_AREA_AGE_COLOR_STOPS: AgeColorStop[] = [
  { minutes: 0, color: "#dc2626", label: "Now" },
  { minutes: 60, color: "#b91c1c", label: "1 h" },
  { minutes: 360, color: "#8b3a2f", label: "6 h" },
  { minutes: 1440, color: "#7a7f85", label: "24 h+" },
];

export const LIGHTNING_POLARITY_RAMPS = [
  { polarity: "+", label: "Positive", stops: LIGHTNING_POSITIVE_AGE_STOPS },
  { polarity: "−", label: "Negative", stops: LIGHTNING_NEGATIVE_AGE_STOPS },
];

// FRP (fire radiative power, MW) → circle radius step stops. Used both in the
// paint expression and the legend size key.
export const HOTSPOT_FRP_SIZE_STOPS = [
  { label: "< 10 MW", radius: 6 },
  { label: "10–50 MW", radius: 9, frpThreshold: 10 },
  { label: "≥ 50 MW", radius: 13, frpThreshold: 50 },
];


const ageColorExpression = (stops: AgeColorStop[]) => [
  "interpolate",
  ["linear"],
  ["coalesce", ["get", "age_minutes"], 0],
  ...stops.flatMap((s) => [s.minutes, s.color]),
];

// FRP-driven radius, composed with a zoom interpolate so points shrink at low
// zoom instead of blanketing the map.
const frpRadiusStep = (scale: number) => [
  "step",
  ["coalesce", ["get", "frp"], 0],
  HOTSPOT_FRP_SIZE_STOPS[0].radius * scale,
  HOTSPOT_FRP_SIZE_STOPS[1].frpThreshold!,
  HOTSPOT_FRP_SIZE_STOPS[1].radius * scale,
  HOTSPOT_FRP_SIZE_STOPS[2].frpThreshold!,
  HOTSPOT_FRP_SIZE_STOPS[2].radius * scale,
];

// Cluster hulls share the hotspot layer's source, so every style filters on
// geometry type (the source carries both the detection points and the hulls).
export const hotspotClusterFillStyle = (id: string) => ({
  id: `${id}-clusters-fill`,
  type: "fill" as const,
  filter: ["==", ["geometry-type"], "Polygon"],
  paint: {
    // Grey when the fire lies outside the project area and so cannot become
    // an ignition.
    "fill-color": ["case", ["get", "creatable"], "#ff7a00", "#94a3b8"],
    "fill-opacity": 0.12,
  },
});

export const hotspotClusterLineStyle = (id: string) => ({
  id: `${id}-clusters-line`,
  type: "line" as const,
  filter: ["==", ["geometry-type"], "Polygon"],
  paint: {
    "line-color": ["case", ["get", "creatable"], "#ff9d3d", "#94a3b8"],
    "line-width": 1.5,
    "line-dasharray": [2, 1.5],
    "line-opacity": 0.85,
  },
});

// A detection point is any Point feature (the source also carries cluster
// hulls / warning areas as polygons). Incident markers live on their own
// layer now — see IncidentsLayer / incidentStyles.ts.
const DETECTION_POINT_FILTER = ["==", ["geometry-type"], "Point"];

export const hotspotLayerStyle = (id: string) => ({
  id,
  type: "circle" as const,
  filter: DETECTION_POINT_FILTER,
  paint: {
    "circle-color": ageColorExpression(HOTSPOT_AGE_COLOR_STOPS),
    "circle-radius": [
      "interpolate",
      ["linear"],
      ["zoom"],
      4,
      frpRadiusStep(0.5),
      9,
      frpRadiusStep(1),
    ],
    "circle-opacity": [
      "interpolate",
      ["linear"],
      ["coalesce", ["get", "age_minutes"], 0],
      0,
      0.95,
      4320,
      0.4,
    ],
  },
});

// Hazards (state emergency incidents & warnings) are coloured by the agency's
// own alert level, not by age: an Emergency Warning issued three hours ago
// still outranks an Advice from a minute ago. `level` is normalized by the
// backend adapters (3 emergency / 2 watch and act / 1 advice / 0 other), so
// the ramp survives each state naming its levels differently.
export interface HazardLevelStyle {
  level: number;
  label: string;
  color: string;
}

export const HAZARD_LEVEL_STYLES: HazardLevelStyle[] = [
  { level: 3, label: "Emergency Warning", color: "#e11d48" },
  { level: 2, label: "Watch and Act", color: "#f59e0b" },
  { level: 1, label: "Advice", color: "#facc15" },
  { level: 0, label: "Incident / other", color: "#60a5fa" },
];

// Mapbox "match" over the level, highest first, falling through to the
// level-0 colour for anything unrecognized.
const hazardLevelColor = () => [
  "match",
  ["coalesce", ["get", "level"], 0],
  3,
  HAZARD_LEVEL_STYLES[0].color,
  2,
  HAZARD_LEVEL_STYLES[1].color,
  1,
  HAZARD_LEVEL_STYLES[2].color,
  HAZARD_LEVEL_STYLES[3].color,
];

// Warning areas travel as sibling polygon features alongside their anchor
// point (one source, styles filtered by geometry type — the hotspot cluster
// hull precedent).
export const hazardAreaFillStyle = (id: string) => ({
  id: `${id}-areas-fill`,
  type: "fill" as const,
  filter: ["!=", ["geometry-type"], "Point"],
  paint: {
    "fill-color": hazardLevelColor(),
    // Deliberately faint: warning areas are often whole districts, and an
    // opaque wash would bury the fuel/vegetation layers underneath.
    "fill-opacity": [
      "match",
      ["coalesce", ["get", "level"], 0],
      3,
      0.22,
      2,
      0.16,
      0.1,
    ],
  },
});

export const hazardAreaLineStyle = (id: string) => ({
  id: `${id}-areas-line`,
  type: "line" as const,
  filter: ["!=", ["geometry-type"], "Point"],
  paint: {
    "line-color": hazardLevelColor(),
    "line-width": ["match", ["coalesce", ["get", "level"], 0], 3, 2.5, 2, 2, 1.2],
    "line-opacity": 0.9,
  },
});

export const hazardLayerStyle = (id: string) => ({
  id,
  type: "circle" as const,
  filter: DETECTION_POINT_FILTER,
  paint: {
    "circle-color": hazardLevelColor(),
    // Size by alert level too, so an emergency warning reads at national zoom
    // where the polygon is a few pixels wide.
    "circle-radius": [
      "interpolate",
      ["linear"],
      ["zoom"],
      4,
      ["match", ["coalesce", ["get", "level"], 0], 3, 6, 2, 5, 3.5],
      9,
      ["match", ["coalesce", ["get", "level"], 0], 3, 11, 2, 9, 6.5],
    ],
    "circle-stroke-width": 1.5,
    "circle-stroke-color": "#0f172a",
    "circle-opacity": 0.95,
  },
});

// Reported ignition points: age-coloured, with a light stroke so they stand
// out over dark vegetation at every zoom.
export const ignitionLayerStyle = (id: string) => ({
  id,
  type: "circle" as const,
  filter: DETECTION_POINT_FILTER,
  paint: {
    "circle-color": ageColorExpression(IGNITION_AGE_COLOR_STOPS),
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 4, 9, 7],
    "circle-stroke-width": 1.5,
    "circle-stroke-color": "#ffffff",
    "circle-opacity": 0.95,
  },
});

// Burnt-area perimeters travel as sibling polygon features beside a centroid
// anchor point (the hazard-layer precedent): fill + outline filtered to
// non-points, a small anchor circle for the point.
export const burntAreaFillStyle = (id: string) => ({
  id: `${id}-areas-fill`,
  type: "fill" as const,
  filter: ["!=", ["geometry-type"], "Point"],
  paint: {
    "fill-color": ageColorExpression(BURNT_AREA_AGE_COLOR_STOPS),
    "fill-opacity": 0.3,
  },
});

export const burntAreaLineStyle = (id: string) => ({
  id: `${id}-areas-line`,
  type: "line" as const,
  filter: ["!=", ["geometry-type"], "Point"],
  paint: {
    "line-color": ageColorExpression(BURNT_AREA_AGE_COLOR_STOPS),
    "line-width": 2,
    "line-opacity": 0.95,
  },
});

export const burntAreaLayerStyle = (id: string) => ({
  id,
  type: "circle" as const,
  filter: DETECTION_POINT_FILTER,
  paint: {
    "circle-color": ageColorExpression(BURNT_AREA_AGE_COLOR_STOPS),
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 3.5, 9, 5],
    "circle-stroke-width": 1.2,
    "circle-stroke-color": "#0f172a",
    "circle-opacity": 0.95,
  },
});

export const lightningLayerStyle = (id: string) => ({
  id,
  type: "circle" as const,
  filter: DETECTION_POINT_FILTER,
  paint: {
    // Strikes with no polarity reported fall through to the negative ramp
    // (the large majority of cloud-to-ground strokes are negative).
    "circle-color": [
      "match",
      ["get", "polarity"],
      "+",
      ageColorExpression(LIGHTNING_POSITIVE_AGE_STOPS),
      ageColorExpression(LIGHTNING_NEGATIVE_AGE_STOPS),
    ],
    "circle-radius": [
      "interpolate",
      ["linear"],
      ["zoom"],
      4,
      3,
      9,
      5.5,
    ],
  },
});
